import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  Drawer,
} from '@mui/material';
import { Favorite, ShoppingCart, Close } from '@mui/icons-material';

interface HeartInsufficientDialogProps {
  open: boolean;
  onClose: () => void;
  onPurchase: () => void;
  requiredHearts: number;
  currentBalance: number;
  actionLabel?: string; // 예: "카드 오픈", "카드 연장"
}

const HeartInsufficientDialog: React.FC<HeartInsufficientDialogProps> = ({
  open,
  onClose,
  onPurchase,
  requiredHearts,
  currentBalance,
  actionLabel,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const shortage = requiredHearts - currentBalance;

  const content = (
    <Box sx={{ animation: 'fadeIn 0.2s ease-in', '@keyframes fadeIn': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'translateY(0)' } } }}>
      {/* 아이콘 + 제목 */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Box sx={{ fontSize: '4rem', lineHeight: 1, mb: 1.5 }}>💔</Box>
        <Typography
          sx={{
            fontSize: '1.5rem',
            fontWeight: 800,
            color: '#0D1F3C',
            lineHeight: 1.3,
          }}
        >
          하트가 부족해요
        </Typography>
        <Typography sx={{ fontSize: '1rem', color: '#8896AB', mt: 0.75 }}>
          {requiredHearts === 1
            ? '카드를 열려면 하트 1개가 필요해요.'
            : requiredHearts === 3
            ? '카드 연장에 하트 3개가 필요해요.'
            : `${actionLabel ?? '이 기능'}에 하트 ${requiredHearts}개가 필요해요.`}
        </Typography>
      </Box>

      {/* 하트 현황 카드 */}
      <Box
        sx={{
          bgcolor: '#FFF5F0',
          border: '2px solid #FFD5C2',
          borderRadius: 2,
          p: 2.5,
          mb: 3,
        }}
      >
        {/* 필요 하트 */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1.5,
          }}
        >
          <Typography sx={{ fontSize: '1.125rem', color: '#4A5A72' }}>
            필요한 하트
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Favorite sx={{ fontSize: 22, color: '#B87D8F' }} />
            <Typography
              sx={{ fontSize: '1.375rem', fontWeight: 800, color: '#B87D8F' }}
            >
              {requiredHearts}개
            </Typography>
          </Box>
        </Box>

        {/* 현재 잔액 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography sx={{ fontSize: '1.125rem', color: '#4A5A72' }}>현재 잔액</Typography>
          <Typography sx={{ fontSize: '1.375rem', fontWeight: 800, color: '#0D1F3C' }}>
            {Math.max(0, currentBalance)}하트
          </Typography>
        </Box>

        {/* 구분선 */}
        <Box sx={{ borderTop: '1px solid #FFD5C2', my: 1.5 }} />

        {/* 부족 수량 */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{ fontSize: '1.125rem', fontWeight: 700, color: '#FF6B35' }}
          >
            부족한 하트
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Favorite sx={{ fontSize: 22, color: '#FF6B35' }} />
            <Typography
              sx={{ fontSize: '1.375rem', fontWeight: 800, color: '#FF6B35' }}
            >
              {shortage}개
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const buttons = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
      <Button
        variant="contained"
        startIcon={<ShoppingCart sx={{ fontSize: 26 }} />}
        onClick={onPurchase}
        fullWidth
        sx={{
          height: 52,
          fontSize: '1.125rem',
          fontWeight: 800,
          borderRadius: 2,
          bgcolor: '#0D1F3C',
          boxShadow: 3,
          '&:hover': {
            boxShadow: 6,
            bgcolor: '#1A3461',
          },
        }}
      >
        하트 구매하러 가기 →
      </Button>
      <Button
        variant="outlined"
        startIcon={<Close />}
        onClick={onClose}
        fullWidth
        sx={{
          height: 48,
          fontSize: '1rem',
          fontWeight: 600,
          borderRadius: 2,
          color: '#8896AB',
          borderColor: '#E2E8F0',
          '&:hover': {
            bgcolor: '#F0F3F8',
            borderColor: '#8896AB',
          },
        }}
      >
        닫기
      </Button>
    </Box>
  );

  // 모바일: 바텀 시트
  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            p: 4,
            pb: 'calc(24px + env(safe-area-inset-bottom))',
          },
        }}
      >
        {content}
        {buttons}
      </Drawer>
    );
  }

  // 데스크탑: 중앙 모달
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxWidth: 420,
        },
      }}
    >
      <DialogContent sx={{ p: 4, pb: 0 }}>
        {content}
      </DialogContent>
      <DialogActions sx={{ px: 4, pb: 4, flexDirection: 'column', gap: 0 }}>
        {buttons}
      </DialogActions>
    </Dialog>
  );
};

export default HeartInsufficientDialog;
